/**
 * IdP Role Mappings (/admin/idp/role-mappings)
 *
 * Sprint 5 S5-C lane companion page. The FI Admin reads and edits each
 * tenant-identity-binding's claimMappingOverrides.roleAllowlist. Unlike
 * the Pending Claims page (claim-by-claim approval with retroactive
 * sweep), this surface is the bulk editor: future sign-ins of any
 * mapped IdP claim string auto-bind to the named Aaim Role without
 * landing in the queue. The retroactive sweep is NOT performed here --
 * the canonical PUT replaces the overrides JSON wholesale and takes
 * effect on the next sign-in.
 *
 * Backend surface (canonical user-management-rbac spec):
 *   PUT /api/v1/admin/idp-bindings/{bindingId}/claim-mapping-overrides
 *     body: ClaimMappingOverrides = { roleAllowlist?, denyList?, groupAllowlist? }
 *     returns: 204 No Content
 *
 * Binding listing comes from identity-provider-registry:
 *   GET /api/v1/tenant-identity-bindings (filtered by caller's tenant)
 *   GET /api/v1/identity-providers (for provider name + issuer URL)
 *
 * Role catalog comes from platform-admin:
 *   GET /api/admin/users/roles (RoleSummary[])
 *
 * ClaimMappingOverrides keys are lowercase IdP role strings; values are the
 * Aaim Role.name exactly as the role catalog returns it (FI_LOAN_OFFICER).
 * The editor lowercases the IdP claim string at save time and sends the role
 * name unchanged. Until 2026-10-14 it lowercased the role name too, and the
 * sign-in resolver, which matched names exactly, resolved no saved mapping.
 * aaim-core refuses a name that is no role (400) or that a tenant may not
 * grant (403), naming the claim keys in ErrorResponse.details.claims; the
 * page shows that message.
 *
 * Note: the spec uses PUT (full replacement of the overrides JSON),
 * not PATCH. Each Save Changes click sends the edited roleAllowlist with the
 * loaded denyList and groupAllowlist unchanged. The editor does not show the
 * group map; until 2026-10-30 a save dropped a stored one (GH #4731).
 *
 * Layout: one accordion item per binding, collapsed by default, its header
 * naming the IdP, the binding status and the mapping count, and marking
 * unsaved edits, which a collapsed item would otherwise hide. The panel
 * holds the claim -> role rows in the shared DataTable. Until 2026-10-20
 * each binding was a full-width card in a vertical stack (GH #1966).
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Heading,
  BodyText,
  Card,
  Button,
  Alert,
  Badge,
  Input,
  Select,
  FormLabel,
  FormHelperText,
  Accordion,
  AccordionItem,
  DataTable,
  type DataTableColumn,
} from '@aaimplatform/ui';
import type { components as UmrComponents } from '@aaimplatform/institutional-types/user-management-rbac';
import type { components as IdpComponents } from '@aaimplatform/institutional-types/identity-provider-registry';
import type { components as PlatformAdminComponents } from '@aaimplatform/institutional-types/platform-admin';
import {
  userManagementRbacClient,
  identityProviderClient,
  platformAdminClient,
} from '../../api/client';
import { useAuthStore } from '../../stores/auth';

type TenantIdentityBinding = IdpComponents['schemas']['TenantIdentityBinding'];
type IdentityProvider = IdpComponents['schemas']['IdentityProvider'];
type RoleSummary = PlatformAdminComponents['schemas']['RoleSummary'];
type ClaimMappingOverrides = UmrComponents['schemas']['ClaimMappingOverrides'];

// ---------------------------------------------------------------------------
// Per-binding editor state
// ---------------------------------------------------------------------------

interface MappingRow {
  rowKey: string;        // stable React key
  idpClaim: string;      // lowercase idp role string
  aaimRoleSlug: string;  // canonical Aaim Role.name slug
}

interface BindingEditorState {
  rows: MappingRow[];
  saveError: string | null;
  saveSuccess: boolean;
  isSubmitting: boolean;
  isDirty: boolean;
}

const EMPTY_EDITOR: BindingEditorState = {
  rows: [],
  saveError: null,
  saveSuccess: false,
  isSubmitting: false,
  isDirty: false,
};

let rowKeyCounter = 0;
const newRowKey = () => `row-${++rowKeyCounter}`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function overridesToRows(overrides: ClaimMappingOverrides | undefined): MappingRow[] {
  const allowlist = overrides?.roleAllowlist;
  if (!allowlist || typeof allowlist !== 'object') return [];
  return Object.entries(allowlist).map(([idpClaim, aaimRoleSlug]) => ({
    rowKey: newRowKey(),
    idpClaim,
    aaimRoleSlug,
  }));
}

/**
 * The PUT body: the edited rows as roleAllowlist, plus the loaded denyList
 * (when it holds an entry) and the loaded groupAllowlist, both unchanged. The
 * PUT replaces the stored overrides whole, so a member left out here is
 * deleted on the server.
 */
export function rowsToOverrides(
  rows: MappingRow[],
  loaded: ClaimMappingOverrides | undefined,
): ClaimMappingOverrides {
  const roleAllowlist: Record<string, string> = {};
  rows.forEach((row) => {
    const key = row.idpClaim.trim().toLowerCase();
    const value = row.aaimRoleSlug.trim();
    if (key && value) {
      roleAllowlist[key] = value;
    }
  });
  const overrides: ClaimMappingOverrides = { roleAllowlist };
  if (loaded?.denyList && loaded.denyList.length > 0) {
    overrides.denyList = loaded.denyList;
  }
  if (loaded?.groupAllowlist) {
    overrides.groupAllowlist = loaded.groupAllowlist;
  }
  return overrides;
}

/**
 * The message for a refused save: the server's message, with the claim keys
 * it names in ErrorResponse.details.claims, so the admin can find the rows.
 */
export function describeSaveError(error: UmrComponents['schemas']['ErrorResponse'] | undefined): string {
  const fallback = 'Failed to save claim mapping overrides';
  if (!error || typeof error.message !== 'string' || error.message.length === 0) return fallback;
  const claims = (error.details as { claims?: unknown } | undefined)?.claims;
  const named = Array.isArray(claims) ? claims.filter((c): c is string => typeof c === 'string') : [];
  return named.length > 0 ? `${error.message} (${named.join(', ')})` : error.message;
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function IdpRoleMappingsPage() {
  const { user } = useAuthStore();
  const tenantId = user?.tenantId ?? undefined;

  const [bindings, setBindings] = useState<TenantIdentityBinding[]>([]);
  const [providersById, setProvidersById] = useState<Record<string, IdentityProvider>>({});
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [bootstrapLoading, setBootstrapLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);

  // Per-binding editor state, keyed by binding id
  const [editorByBinding, setEditorByBinding] = useState<Record<string, BindingEditorState>>({});

  // Bootstrap: bindings + providers + roles
  useEffect(() => {
    let canceled = false;
    async function bootstrap() {
      setBootstrapLoading(true);
      setBootstrapError(null);
      try {
        const [bindingResult, providerResult, rolesResult] = await Promise.all([
          identityProviderClient.GET('/api/v1/tenant-identity-bindings', {
            params: { query: { tenantId } },
          }),
          identityProviderClient.GET('/api/v1/identity-providers', {
            params: { query: {} },
          }),
          platformAdminClient.GET('/users/roles'),
        ]);
        if (canceled) return;
        if (bindingResult.error) throw new Error('Failed to load identity provider bindings');
        if (providerResult.error) throw new Error('Failed to load identity providers');
        if (rolesResult.error) throw new Error('Failed to load role catalog');
        const bindingList = bindingResult.data?.data ?? [];
        const providerList = providerResult.data?.data ?? [];
        const providerMap: Record<string, IdentityProvider> = {};
        providerList.forEach((p) => {
          providerMap[p.id] = p;
        });
        const initialEditors: Record<string, BindingEditorState> = {};
        bindingList.forEach((binding) => {
          initialEditors[binding.id] = {
            ...EMPTY_EDITOR,
            rows: overridesToRows(binding.claimMappingOverrides),
          };
        });
        setBindings(bindingList);
        setProvidersById(providerMap);
        setRoles(rolesResult.data?.data ?? []);
        setEditorByBinding(initialEditors);
      } catch (err) {
        if (canceled) return;
        setBootstrapError(err instanceof Error ? err.message : 'Failed to load identity providers');
      } finally {
        if (!canceled) setBootstrapLoading(false);
      }
    }
    void bootstrap();
    return () => {
      canceled = true;
    };
  }, [tenantId]);

  // Role select options -- prefer FI-tenant slugs first, then everything else
  const roleOptions = useMemo(() => {
    const sorted = [...roles].sort((a, b) => {
      const aFi = a.name.toLowerCase().startsWith('fi_') ? 0 : 1;
      const bFi = b.name.toLowerCase().startsWith('fi_') ? 0 : 1;
      if (aFi !== bFi) return aFi - bFi;
      return a.displayName.localeCompare(b.displayName);
    });
    return sorted;
  }, [roles]);

  // Editor mutators
  const updateEditor = useCallback(
    (bindingId: string, mutator: (prev: BindingEditorState) => BindingEditorState) => {
      setEditorByBinding((prev) => {
        const current = prev[bindingId] ?? EMPTY_EDITOR;
        return { ...prev, [bindingId]: mutator(current) };
      });
    },
    [],
  );

  const handleAddMapping = (bindingId: string) => {
    updateEditor(bindingId, (prev) => ({
      ...prev,
      rows: [...prev.rows, { rowKey: newRowKey(), idpClaim: '', aaimRoleSlug: '' }],
      isDirty: true,
      saveSuccess: false,
      saveError: null,
    }));
  };

  const handleRemoveMapping = (bindingId: string, rowKey: string) => {
    updateEditor(bindingId, (prev) => ({
      ...prev,
      rows: prev.rows.filter((row) => row.rowKey !== rowKey),
      isDirty: true,
      saveSuccess: false,
      saveError: null,
    }));
  };

  const handleRowChange = (
    bindingId: string,
    rowKey: string,
    field: 'idpClaim' | 'aaimRoleSlug',
    value: string,
  ) => {
    updateEditor(bindingId, (prev) => ({
      ...prev,
      rows: prev.rows.map((row) =>
        row.rowKey === rowKey ? { ...row, [field]: value } : row,
      ),
      isDirty: true,
      saveSuccess: false,
      saveError: null,
    }));
  };

  const handleResetMappings = (bindingId: string) => {
    const binding = bindings.find((b) => b.id === bindingId);
    if (!binding) return;
    updateEditor(bindingId, () => ({
      ...EMPTY_EDITOR,
      rows: overridesToRows(binding.claimMappingOverrides),
    }));
  };

  const handleSaveMappings = async (bindingId: string) => {
    const editor = editorByBinding[bindingId];
    const binding = bindings.find((b) => b.id === bindingId);
    if (!editor || !binding) return;

    // Client-side validation: every row must have both fields and no duplicates
    const claimsSeen = new Set<string>();
    for (const row of editor.rows) {
      const claim = row.idpClaim.trim().toLowerCase();
      const slug = row.aaimRoleSlug.trim();
      if (!claim || !slug) {
        updateEditor(bindingId, (prev) => ({
          ...prev,
          saveError: 'Every row needs an IdP claim string and an Aaim role.',
        }));
        return;
      }
      if (claimsSeen.has(claim)) {
        updateEditor(bindingId, (prev) => ({
          ...prev,
          saveError: `Duplicate IdP claim "${claim}". Each claim string maps to exactly one role.`,
        }));
        return;
      }
      claimsSeen.add(claim);
    }

    updateEditor(bindingId, (prev) => ({
      ...prev,
      isSubmitting: true,
      saveError: null,
      saveSuccess: false,
    }));

    try {
      // The PUT replaces the entire overrides JSON, so the loaded denyList
      // and groupAllowlist go back unchanged with the edited rows; without
      // them a save would wipe both on the server.
      const body = rowsToOverrides(editor.rows, binding.claimMappingOverrides);
      const result = await userManagementRbacClient.PUT(
        '/api/v1/admin/idp-bindings/{bindingId}/claim-mapping-overrides',
        { params: { path: { bindingId } }, body },
      );
      if (result.error) throw new Error(describeSaveError(result.error));

      // On success, update the in-memory binding so a subsequent reset
      // restores to the saved state, not the server-loaded state.
      setBindings((prev) =>
        prev.map((b) =>
          b.id === bindingId
            ? { ...b, claimMappingOverrides: { ...b.claimMappingOverrides, ...body } }
            : b,
        ),
      );
      updateEditor(bindingId, (prev) => ({
        ...prev,
        isSubmitting: false,
        isDirty: false,
        saveSuccess: true,
      }));
    } catch (err) {
      updateEditor(bindingId, (prev) => ({
        ...prev,
        isSubmitting: false,
        saveError: err instanceof Error ? err.message : 'Failed to save claim mapping overrides',
      }));
    }
  };

  // Render

  if (bootstrapLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); the body opens
            with the descriptive subtitle, no redundant H1. */}
        <BodyText size="sm" color="tertiary">
          Map IdP-emitted role claim strings to canonical Aaim roles per
          identity provider binding. Future sign-ins of any mapped claim
          auto-bind to the named role and skip the Pending Claims queue.
          Save Changes replaces the binding&apos;s claim mapping overrides
          wholesale; existing claims that are not in the table no longer
          auto-bind.
        </BodyText>
      </div>

      {bootstrapError && (
        <div className="mb-4">
          <Alert type="error">{bootstrapError}</Alert>
        </div>
      )}

      {bindings.length === 0 ? (
        <Card>
          <div className="px-6 py-10 text-center">
            <Heading level={2} size="subsection" className="mb-2">
              No identity providers configured.
            </Heading>
            <BodyText size="sm" color="tertiary">
              Configure an identity provider on the Identity Provider page
              before editing role mappings.
            </BodyText>
          </div>
        </Card>
      ) : (
        <Accordion>
          {bindings.map((binding) => {
            const provider = providersById[binding.identityProviderId];
            const editor = editorByBinding[binding.id] ?? EMPTY_EDITOR;
            const providerName = provider?.name ?? `Binding ${binding.id.slice(0, 8)}`;
            const issuerUrl = provider?.issuerUrl ?? provider?.providerFamily ?? 'unknown';
            const lastSync = binding.updatedAt
              ? new Date(binding.updatedAt).toLocaleString()
              : 'never';
            const mappingCount = editor.rows.length;

            // Each cell edits the row in place; rowKey keeps an input's
            // identity, and so its focus, across the re-render a keystroke
            // causes.
            const columns: DataTableColumn<MappingRow>[] = [
              {
                header: 'IdP Claim String',
                accessor: 'idpClaim',
                width: '45%',
                cell: (_value, row) => (
                  <Input
                    aria-label="IdP claim string"
                    value={row.idpClaim}
                    onChange={(e) =>
                      handleRowChange(binding.id, row.rowKey, 'idpClaim', e.target.value)
                    }
                    placeholder="cu_loan_officer"
                  />
                ),
              },
              {
                header: 'Aaim Role',
                accessor: 'aaimRoleSlug',
                width: '45%',
                cell: (_value, row) => (
                  <Select
                    aria-label="Aaim role"
                    value={row.aaimRoleSlug}
                    onChange={(e) =>
                      handleRowChange(binding.id, row.rowKey, 'aaimRoleSlug', e.target.value)
                    }
                  >
                    <option value="">Select an Aaim role...</option>
                    {roleOptions.map((role) => (
                      <option key={role.id} value={role.name}>
                        {role.displayName} ({role.name})
                      </option>
                    ))}
                  </Select>
                ),
              },
              {
                header: 'Actions',
                accessor: 'rowKey',
                align: 'right',
                cell: (_value, row) => (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveMapping(binding.id, row.rowKey)}
                    disabled={editor.isSubmitting}
                  >
                    Remove
                  </Button>
                ),
              },
            ];

            return (
              <AccordionItem
                key={binding.id}
                id={`idp-binding-${binding.id}`}
                headingLevel={2}
                title={providerName}
                meta={
                  <>
                    {binding.isPrimary && (
                      <Badge variant="info" size="sm">
                        Primary
                      </Badge>
                    )}
                    <Badge
                      variant={binding.status === 'active' ? 'success' : 'warning'}
                      size="sm"
                    >
                      {binding.status}
                    </Badge>
                    <span>
                      {mappingCount} {mappingCount === 1 ? 'mapping' : 'mappings'}
                    </span>
                    {editor.isDirty && (
                      <Badge variant="warning" size="sm">
                        Unsaved changes
                      </Badge>
                    )}
                  </>
                }
                panelClassName="border-t border-recessed"
              >
                <div className="px-6 py-4 space-y-4">
                  <div>
                    <BodyText size="sm" color="tertiary">
                      Issuer: {issuerUrl}
                    </BodyText>
                    <BodyText size="sm" color="tertiary">
                      Last updated: {lastSync}
                    </BodyText>
                  </div>

                  {editor.saveError && (
                    <Alert type="error">{editor.saveError}</Alert>
                  )}
                  {editor.saveSuccess && (
                    <Alert type="success">
                      Claim mapping overrides saved. Future sign-ins of these
                      claim strings now auto-bind to the named roles.
                    </Alert>
                  )}

                  <DataTable<MappingRow>
                    columns={columns}
                    data={editor.rows}
                    rowKey="rowKey"
                    className="p-0"
                    emptyMessage='No role mappings configured for this binding. Click "Add Mapping" to map an IdP claim string to an Aaim role.'
                  />

                  <FormLabel className="sr-only">Add a new mapping row</FormLabel>
                  <FormHelperText>
                    IdP claim strings are normalized to lowercase on save.
                    Aaim role slugs come from your institution’s role catalog.
                  </FormHelperText>
                </div>

                <div className="px-6 py-4 border-t border-recessed flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAddMapping(binding.id)}
                    disabled={editor.isSubmitting}
                  >
                    Add Mapping
                  </Button>
                  <div className="flex gap-3">
                    <Button
                      variant="ghost"
                      onClick={() => handleResetMappings(binding.id)}
                      disabled={editor.isSubmitting || !editor.isDirty}
                    >
                      Reset
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => void handleSaveMappings(binding.id)}
                      disabled={editor.isSubmitting || !editor.isDirty}
                    >
                      {editor.isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </div>
  );
}

// Suppress unused-import warnings for type-only imports
export type { UmrComponents as _IdpRoleMappingsUmrComponents };
